import {
  buildDirectorySearchExpansion,
  type DirectorySearchExpansion,
} from "@/lib/search/searchExpansion";

export type { DirectorySearchExpansion };

export type SearchIntent = {
  intent: "find_business";
  originalQuery: string;
  cleanQuery: string;
  tokens: string[];
  phraseTokens: string[];
  itemHints: string[];
  categoryHints: string[];
  locationHints: string[];
  serviceHints: string[];
  mustHave: string[];
  nearLocation: string | null;
  openNow: boolean;
  timeIntent: "open_now" | null;
  confidence: number;
};

type CachedIntent = {
  expiresAt: number;
  intent: SearchIntent;
};

const REQUEST_TIMEOUT_MS = 6500;
const CACHE_TTL_MS = 10 * 60 * 1000;
const MAX_QUERY_LENGTH = 180;
const MAX_DIRECTORY_QUERY_WORDS = 8;

const NATURAL_LANGUAGE_PATTERNS = [
  /\b(i need|i want|looking for|look for|find me|where can i|do you have)\b/i,
  /\b(near|nearby|around|within|in)\b/i,
  /\b(for sale|services?|provider|designer|supplier|rental|hire)\b/i,
  /\b(and|with)\b/i,
];

const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "around",
  "at",
  "by",
  "for",
  "from",
  "i",
  "im",
  "in",
  "looking",
  "near",
  "nearby",
  "need",
  "now",
  "of",
  "on",
  "open",
  "or",
  "still",
  "the",
  "today",
  "tonight",
  "to",
  "want",
  "with",
]);

const intentCache = new Map<string, CachedIntent>();

function cleanText(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const clean = value.replace(/\s+/g, " ").trim();
  return clean ? clean : null;
}

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(value: string): string[] {
  const normalized = normalizeSearchText(value);
  if (!normalized) return [];

  return normalized
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length > 1 && !SEARCH_STOP_WORDS.has(token));
}

function uniqueStrings(values: string[], maxCount = Number.POSITIVE_INFINITY) {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const value of values) {
    const clean = normalizeSearchText(value);
    if (!clean || seen.has(clean)) continue;

    seen.add(clean);
    out.push(clean);

    if (out.length >= maxCount) break;
  }

  return out;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return uniqueStrings(
    value
      .map((item) => cleanText(item))
      .filter((item): item is string => item !== null),
    14
  );
}

function hasOpenNowIntent(
  originalQuery: string,
  row: Record<string, unknown>
): boolean {
  if (row.openNow === true) return true;

  const timeIntent = cleanText(row.timeIntent);
  if (timeIntent) {
    const normalizedTimeIntent = normalizeSearchText(timeIntent).replace(
      /\s+/g,
      "_"
    );

    if (normalizedTimeIntent === "open_now") return true;
  }

  return /\b(open now|open right now|currently open|open today|open tonight|still open)\b/.test(
    normalizeSearchText(originalQuery)
  );
}

function normalizeIntent(value: unknown, originalQuery: string): SearchIntent | null {
  if (!value || typeof value !== "object") return null;

  const row = value as Record<string, unknown>;
  const tokens = normalizeStringArray(row.tokens);
  const phraseTokens = normalizeStringArray(row.phraseTokens);
  const itemHints = normalizeStringArray(row.itemHints);
  const categoryHints = normalizeStringArray(row.categoryHints);
  const locationHints = normalizeStringArray(row.locationHints);
  const serviceHints = normalizeStringArray(row.serviceHints);
  const mustHave = normalizeStringArray(row.mustHave);
  const openNow = hasOpenNowIntent(originalQuery, row);
  const cleanQuery =
    cleanText(row.cleanQuery) ??
    uniqueStrings([
      ...mustHave,
      ...itemHints,
      ...serviceHints,
      ...categoryHints,
      ...locationHints,
      ...tokens,
    ]).join(" ");

  const confidence = Number(row.confidence);
  const normalizedCleanQuery = normalizeSearchText(cleanQuery);

  if (!normalizedCleanQuery && tokens.length === 0 && mustHave.length === 0) {
    return null;
  }

  return {
    intent: "find_business",
    originalQuery,
    cleanQuery: normalizedCleanQuery || normalizeSearchText(originalQuery),
    tokens,
    phraseTokens,
    itemHints,
    categoryHints,
    locationHints,
    serviceHints,
    mustHave,
    nearLocation: cleanText(row.nearLocation)
      ? normalizeSearchText(String(row.nearLocation))
      : null,
    openNow,
    timeIntent: openNow ? "open_now" : null,
    confidence: Number.isFinite(confidence)
      ? Math.max(0, Math.min(1, confidence))
      : 0.5,
  };
}

function getEndpoint(): string | null {
  return cleanText(process.env.EXPO_PUBLIC_SEARCH_INTENT_URL);
}

function getCacheKey(query: string): string {
  return normalizeSearchText(query);
}

export function shouldUseSmartSearch(query: string): boolean {
  const normalized = normalizeSearchText(query);
  if (!normalized || normalized.length > MAX_QUERY_LENGTH) return false;

  const words = normalized.split(" ").filter(Boolean);
  if (words.length >= 3) return true;
  if (words.length < 2 || normalized.length < 5) return false;

  return NATURAL_LANGUAGE_PATTERNS.some((pattern) => pattern.test(query));
}

export async function parseSearchIntent(
  query: string
): Promise<SearchIntent | null> {
  const trimmed = cleanText(query);
  if (!trimmed || !shouldUseSmartSearch(trimmed)) return null;

  const endpoint = getEndpoint();
  if (!endpoint) {
    if (__DEV__) {
      console.warn("Missing EXPO_PUBLIC_SEARCH_INTENT_URL");
    }
    return null;
  }

  const cacheKey = getCacheKey(trimmed);
  const cached = intentCache.get(cacheKey);

  if (cached && cached.expiresAt > Date.now()) {
    return cached.intent;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query: trimmed }),
      signal: controller.signal,
    });

    if (!response.ok) {
      if (__DEV__) {
        console.warn("Search intent parser returned HTTP", response.status);
      }
      return null;
    }

    const data = (await response.json()) as unknown;
    const intent = normalizeIntent(data, trimmed);

    if (!intent) return null;

    intentCache.set(cacheKey, {
      expiresAt: Date.now() + CACHE_TTL_MS,
      intent,
    });

    if (__DEV__) {
      console.log("Smart search intent parsed", {
        tokenCount: intent.tokens.length,
        phraseCount: intent.phraseTokens.length,
        hasNearLocation: !!intent.nearLocation,
        confidence: intent.confidence,
      });
    }

    return intent;
  } catch {
    if (__DEV__) {
      console.warn("Search intent parser failed.");
    }

    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export function buildSmartDirectorySearchQuery(
  originalQuery: string,
  intent: SearchIntent | null
): string {
  if (!intent) return originalQuery.trim();

  const expansion = buildDirectorySearchExpansion(originalQuery, intent);
  const selected =
    expansion.prefixTerms.length > 0
      ? expansion.prefixTerms
      : [
          ...intent.mustHave,
          ...intent.itemHints,
          ...intent.serviceHints,
          ...intent.categoryHints,
          ...intent.phraseTokens,
          ...intent.tokens,
          intent.cleanQuery,
        ];

  const words = uniqueStrings(
    selected.flatMap((value) => tokenize(value)),
    MAX_DIRECTORY_QUERY_WORDS
  );

  const smartQuery = words.join(" ").trim();
  return smartQuery || originalQuery.trim();
}
